import {z} from 'zod'

import {FormValidationInput} from '../../../types'
import {ErrorCodes} from '../../common/validation/errorCodes'
import {
  AdditionalNotesForm,
  AdditionalNotesConfiguration,
  DriverInstructionsForm,
  DriverInstructionsConfiguration,
  PoNumberForm,
  PoNumberConfiguration
} from '../types'

/**
 * Validates additional notes field
 */
export const validateAdditionalNotes = (
  data: AdditionalNotesForm,
  ctx: z.RefinementCtx,
  input?: FormValidationInput<AdditionalNotesConfiguration>
) => {
  const configuration = input?.configuration?.additionalNotes

  // Skip validation if field is not enabled
  if (!configuration?.enabled) {
    return
  }

  const value = data.additionalNotes

  // Check if field is required and empty
  if (configuration.enabled && (!value || value.trim().length === 0)) {
    // For now, text fields are optional even when enabled
    // This can be made configurable in the future if needed
    return
  }

  // Check minimum length if specified
  if (value && configuration.minLength && value.length < configuration.minLength) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['additionalNotes'],
      message: `${ErrorCodes.additionalNotes.required}-min-length`
    })
    return
  }

  // Check maximum length if specified
  if (value && configuration.maxLength && value.length > configuration.maxLength) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['additionalNotes'],
      message: `${ErrorCodes.additionalNotes.required}-max-length`
    })
    return
  }
}

/**
 * Validates driver instructions field
 */
export const validateDriverInstructions = (
  data: DriverInstructionsForm,
  ctx: z.RefinementCtx,
  input?: FormValidationInput<DriverInstructionsConfiguration>
) => {
  const configuration = input?.configuration?.driverInstructions

  // Skip validation if field is not enabled
  if (!configuration?.enabled) {
    return
  }

  const value = data.driverInstructions

  // Check if field is required and empty
  if (configuration.enabled && (!value || value.trim().length === 0)) {
    // For now, text fields are optional even when enabled
    // This can be made configurable in the future if needed
    return
  }

  // Check minimum length if specified
  if (value && configuration.minLength && value.length < configuration.minLength) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['driverInstructions'],
      message: `${ErrorCodes.driverInstructions.required}-min-length`
    })
    return
  }

  // Check maximum length if specified
  if (value && configuration.maxLength && value.length > configuration.maxLength) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['driverInstructions'],
      message: `${ErrorCodes.driverInstructions.required}-max-length`
    })
    return
  }
}

/**
 * Validates PO number field
 */
export const validatePoNumber = (
  data: PoNumberForm,
  ctx: z.RefinementCtx,
  input?: FormValidationInput<PoNumberConfiguration>
) => {
  const configuration = input?.configuration?.poNumber

  // Skip validation if field is not enabled
  if (!configuration?.enabled) {
    return
  }

  const value = data.poNumber

  // PO Number is required when minLength > 0
  if (
    configuration.minLength !== undefined &&
    configuration.minLength > 0 &&
    (!value || value.trim().length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['poNumber'],
      message: ErrorCodes.poNumber.required
    })
    return
  }

  // Check minimum length if specified
  if (
    value &&
    configuration.minLength !== undefined &&
    configuration.minLength > 0 &&
    value.length < configuration.minLength
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['poNumber'],
      message: `${ErrorCodes.poNumber.required}-min-length`
    })
    return
  }

  // Check maximum length if specified
  if (value && configuration.maxLength && value.length > configuration.maxLength) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['poNumber'],
      message: `${ErrorCodes.poNumber.required}-max-length`
    })
    return
  }
}
